import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, ShoppingCart, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";

function Block({ block }) {
  switch (block.type) {
    case "p":
      return <p className="text-gray-700 leading-relaxed">{block.text}</p>;
    case "h2":
      return (
        <h2 className="font-display text-2xl md:text-3xl tracking-tighter font-black mt-12 mb-5 leading-tight">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="font-display text-lg md:text-xl tracking-tight font-bold mt-8 mb-3 text-gray-900">
          {block.text}
        </h3>
      );
    case "ul":
      return (
        <ul className="space-y-2 my-4 list-disc pl-6 text-gray-700">
          {block.items.map((it, i) => (
            <li key={i} className="leading-relaxed">
              {it}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="space-y-2 my-4 list-decimal pl-6 text-gray-700">
          {block.items.map((it, i) => (
            <li key={i} className="leading-relaxed">
              {it}
            </li>
          ))}
        </ol>
      );
    case "table":
      return (
        <div className="my-6 overflow-x-auto border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {block.rows[0].map((c, i) => (
                  <th key={i} className="text-left px-4 py-3 font-semibold text-gray-900">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.slice(1).map((r, i) => (
                <tr key={i} className="border-b border-gray-100 last:border-b-0">
                  {r.map((c, j) => (
                    <td key={j} className="px-4 py-3 text-gray-700">
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "callout":
      return (
        <aside className="my-6 border-l-4 border-[#059669] bg-emerald-50 p-5">
          <p className="text-sm text-gray-800 leading-relaxed">{block.text}</p>
        </aside>
      );
    case "cta":
      return (
        <aside className="my-10 border border-gray-200 bg-gray-50 p-7">
          <p className="text-base text-gray-800 leading-relaxed mb-5">{block.text}</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/urunler"
              className="inline-flex items-center gap-2 bg-[#059669] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-emerald-700 transition-colors"
            >
              <ShoppingCart size={14} /> Ürünler
            </Link>
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 border border-black text-black px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-black hover:text-white transition-colors"
            >
              <MessageCircle size={14} /> İletişim
            </Link>
          </div>
        </aside>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="blog-post-loading" className="max-w-3xl mx-auto px-6 py-32 text-center text-gray-500">
        Yükleniyor...
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div data-testid="blog-post-404" className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-black">Yazı bulunamadı</h1>
        <Link
          to="/rehber"
          className="mt-6 inline-flex items-center gap-2 text-[#059669] hover:underline text-sm"
        >
          <ArrowLeft size={14} /> Rehber'e dön
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <SEO
        title={post.title}
        description={post.description}
        path={`/rehber/${post.slug}`}
        type="article"
      />

      <article className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <Link
          to="/rehber"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 hover:text-[#059669]"
        >
          <ArrowLeft size={12} /> Tüm Rehberler
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 mb-10 pb-10 border-b border-gray-200"
        >
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] font-mono-fd mb-5">
            <span className="text-[#059669] font-semibold">{post.category}</span>
            <span className="text-gray-300">·</span>
            <span className="text-gray-500 inline-flex items-center gap-1.5">
              <Clock size={12} /> {post.reading_time} dk okuma
            </span>
            <span className="text-gray-300">·</span>
            <span className="text-gray-500">{post.published_at}</span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tighter font-black leading-[1.05]">
            {post.title}
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed italic">{post.excerpt}</p>
        </motion.header>

        <div className="prose-content space-y-1">
          {post.content.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </div>

        <div className="mt-16 pt-10 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/rehber"
            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-gray-700 hover:text-[#059669]"
          >
            <ArrowLeft size={14} /> Daha fazla rehber
          </Link>
          <Link
            to="/urunler"
            className="inline-flex items-center gap-2 bg-[#059669] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-emerald-700 transition-colors"
          >
            <ShoppingCart size={14} /> Ürünleri Gör
          </Link>
        </div>
      </article>
    </div>
  );
}
